import { useCallback, useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import {
  selectViewMode,
  selectDisplayMode,
  selectCurrentDate,
  selectCalendarGranularity,
  selectCalendarVisibleHours,
  selectClusterBusyHours,
  selectGroupByMode,
  selectGroupByLabelGroupId,
  selectSplitLanesByProvider,
  selectCurrentDateRange,
  selectCalendarViewState,
  selectIsRequestsPanelOpen,
  selectIsSidebarOpen,
  selectIsTutorialMode,
  selectMockAppointments,
  selectOptimisticAppointmentPatches,
  selectWeekStartDay,
} from './selectors';
import {
  setViewMode,
  setDisplayMode,
  setCurrentDate,
  requestScrollToNow,
  setCalendarGranularity,
  setCalendarVisibleHours,
  setClusterBusyHours,
  setGroupBy,
  setSplitLanesByProvider,
  setSidebarOpen,
  setRequestsPanelOpen,
  setTutorialMode,
  setMockAppointments,
  setWeekStartDay,
  clearOptimisticAppointmentPatch,
} from './slice';
import type { CalendarGroupByMode, WeekStartDay } from './slice';
import {
  useGetScheduleEventsQuery,
  useGetNextAppointmentQuery,
  convertToGeneratedApiParams,
} from './api';
import { ViewMode, DisplayMode, AppointmentQueryParams } from '../types';
import { AppointmentResponse } from '@store/generated/appointments';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { useOrgScheduleAccess } from '../hooks/useOrgScheduleAccess';
import { useOrgScheduleFilters } from '../hooks/useOrgScheduleFilters';

/**
 * Hook for managing appointment view controls (calendar navigation, view modes)
 */
export function useAppointmentViewControls() {
  const dispatch = useAppDispatch();
  const calendarViewState = useAppSelector(selectCalendarViewState);
  const calendarGranularity = useAppSelector(selectCalendarGranularity);
  const weekStartDay = useAppSelector(selectWeekStartDay);
  const calendarVisibleHours = useAppSelector(selectCalendarVisibleHours);
  const clusterBusyHours = useAppSelector(selectClusterBusyHours);

  const setAppointmentViewMode = useCallback(
    (mode: ViewMode) => {
      dispatch(setViewMode(mode));
    },
    [dispatch],
  );

  const setAppointmentDisplayMode = useCallback(
    (mode: DisplayMode) => {
      dispatch(setDisplayMode(mode));
    },
    [dispatch],
  );

  const setAppointmentCurrentDate = useCallback(
    (date: Date | string) => {
      const isoString = typeof date === 'string' ? date : date.toISOString();
      dispatch(setCurrentDate(isoString));
    },
    [dispatch],
  );

  const setAppointmentCalendarGranularity = useCallback(
    (granularity: { step: 1 | 5 | 10 | 15; timeslots: 1 | 2 | 3 | 4 | 6 | 12 }) => {
      dispatch(setCalendarGranularity(granularity));
    },
    [dispatch],
  );

  const setAppointmentWeekStartDay = useCallback(
    (day: WeekStartDay) => {
      dispatch(setWeekStartDay(day));
    },
    [dispatch],
  );

  const setAppointmentVisibleHours = useCallback(
    (hours: { startHour: number; endHour: number }) => {
      dispatch(setCalendarVisibleHours(hours));
    },
    [dispatch],
  );

  const setAppointmentClusterBusyHours = useCallback(
    (enabled: boolean) => {
      dispatch(setClusterBusyHours(enabled));
    },
    [dispatch],
  );

  const requestAppointmentScrollToNow = useCallback(() => {
    dispatch(requestScrollToNow());
  }, [dispatch]);

  return {
    ...calendarViewState,
    calendarGranularity,
    weekStartDay,
    calendarVisibleHours,
    clusterBusyHours,
    setViewMode: setAppointmentViewMode,
    setDisplayMode: setAppointmentDisplayMode,
    setCurrentDate: setAppointmentCurrentDate,
    setCalendarGranularity: setAppointmentCalendarGranularity,
    setWeekStartDay: setAppointmentWeekStartDay,
    setCalendarVisibleHours: setAppointmentVisibleHours,
    setClusterBusyHours: setAppointmentClusterBusyHours,
    requestScrollToNow: requestAppointmentScrollToNow,
  };
}

/**
 * Hook for the day-view "label lens" control (off / group / label) + the
 * provider-split toggle.
 */
export function useCalendarGroupBy() {
  const dispatch = useAppDispatch();
  const groupByMode = useAppSelector(selectGroupByMode);
  const groupByLabelGroupId = useAppSelector(selectGroupByLabelGroupId);
  const splitLanesByProvider = useAppSelector(selectSplitLanesByProvider);

  const setGroupByValue = useCallback(
    (mode: CalendarGroupByMode, labelGroupId: string | null = null) => {
      dispatch(setGroupBy({ mode, labelGroupId }));
    },
    [dispatch],
  );

  const setSplitLanes = useCallback(
    (enabled: boolean) => {
      dispatch(setSplitLanesByProvider(enabled));
    },
    [dispatch],
  );

  return {
    groupByMode,
    groupByLabelGroupId,
    splitLanesByProvider,
    setGroupBy: setGroupByValue,
    setSplitLanesByProvider: setSplitLanes,
  };
}

// Filter hook removed - sorting is now part of view controls

/**
 * Hook for managing appointment interactions (sidebar)
 */
export function useAppointmentInteractions() {
  const dispatch = useAppDispatch();
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const isRequestsPanelOpen = useAppSelector(selectIsRequestsPanelOpen);

  const setAppointmentSidebarOpen = useCallback(
    (isOpen: boolean) => {
      dispatch(setSidebarOpen(isOpen));
    },
    [dispatch],
  );

  const setAppointmentRequestsPanelOpen = useCallback(
    (isOpen: boolean) => {
      dispatch(setRequestsPanelOpen(isOpen));
    },
    [dispatch],
  );

  return {
    isSidebarOpen,
    setSidebarOpen: setAppointmentSidebarOpen,
    isRequestsPanelOpen,
    setRequestsPanelOpen: setAppointmentRequestsPanelOpen,
  };
}

/**
 * Hook for managing tutorial mode
 */
export function useAppointmentTutorialMode() {
  const dispatch = useAppDispatch();
  const isTutorialMode = useAppSelector(selectIsTutorialMode);

  const setAppointmentTutorialMode = useCallback(
    (isActive: boolean) => {
      dispatch(setTutorialMode(isActive));

      if (isActive) {
        // Generate mock appointments when enabling tutorial mode
        const mockAppointments = createMockAppointments();
        dispatch(setMockAppointments(mockAppointments));
      }
    },
    [dispatch],
  );

  return {
    isTutorialMode,
    setTutorialMode: setAppointmentTutorialMode,
  };
}

/**
 * Hook that provides the current appointments query parameters based on state
 */
export function useAppointmentQueryParams(): AppointmentQueryParams {
  const viewMode = useAppSelector(selectViewMode);
  const currentDate = useAppSelector(selectCurrentDate);
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive, selectedProviderIds, selectedPatientIds } = useOrgScheduleFilters();
  const isCalendarV2Enabled = useFeatureGate(FeatureGates.CALENDAR_V2);

  return useMemo(() => {
    // Calculate date range based on the view mode
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (viewMode === 'day') {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else if (viewMode === 'week') {
      // Start from Sunday of the current week
      const day = currentDate.getDay();
      start.setDate(currentDate.getDate() - day);
      start.setHours(0, 0, 0, 0);
      // End on Saturday of the current week
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (viewMode === 'month') {
      // Start from the 1st day of the month
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      // End on the last day of the month
      end.setFullYear(currentDate.getFullYear());
      end.setMonth(currentDate.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    }

    const params: AppointmentQueryParams = {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    };

    if (isCalendarV2Enabled) {
      params.include_cancelled = true;
    }

    if (hasOrgScheduleAccess && isOrgViewActive) {
      if (selectedProviderIds === null) {
        // null = uninitialized (pre-clinician-load), fetch all until initialized
        params.all_providers = true;
      } else if (selectedProviderIds.length > 0) {
        // User has specific providers selected
        params.provider_ids = selectedProviderIds;
      }
      // [] = user deselected all → no provider params → no results
      if (selectedPatientIds.length > 0) {
        params.patient_ids = selectedPatientIds;
      }
    }

    return params;
  }, [
    viewMode,
    currentDate,
    hasOrgScheduleAccess,
    isOrgViewActive,
    selectedProviderIds,
    selectedPatientIds,
    isCalendarV2Enabled,
  ]);
}

/**
 * Main hook that replaces useAppointments from the context
 * Now properly separates client state (Redux) from server state (RTK Query)
 */
export function useAppointments() {
  const dispatch = useAppDispatch();

  // Client state from Redux
  const isTutorialMode = useAppSelector(selectIsTutorialMode);
  const mockAppointments = useAppSelector(selectMockAppointments);
  const optimisticAppointmentPatches = useAppSelector(selectOptimisticAppointmentPatches);
  const viewMode = useAppSelector(selectViewMode);
  const displayMode = useAppSelector(selectDisplayMode);

  // Get current query parameters based on client state
  const queryParams = useAppointmentQueryParams();

  // Skip the query when user explicitly deselected all providers (empty calendar)
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive, selectedProviderIds } = useOrgScheduleFilters();
  const hasNoProviders =
    hasOrgScheduleAccess &&
    isOrgViewActive &&
    Array.isArray(selectedProviderIds) &&
    selectedProviderIds.length === 0;

  // Server state from RTK Query
  const {
    data: scheduleEventsData,
    isLoading: isScheduleEventsLoading,
    isFetching: isScheduleEventsFetching,
    isError: isScheduleEventsError,
    refetch: refetchScheduleEvents,
  } = useGetScheduleEventsQuery(convertToGeneratedApiParams(queryParams), {
    skip: isTutorialMode || hasNoProviders,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true, // Prevent stale data when navigating back
  });

  // Apply client-side filtering to server data
  const filteredAppointments = useMemo(() => {
    const appointmentItems = scheduleEventsData?.appointments ?? [];
    if (isTutorialMode || hasNoProviders || appointmentItems.length === 0) {
      return [];
    }
    return filterAppointmentsForView(appointmentItems, queryParams, viewMode);
  }, [hasNoProviders, isTutorialMode, queryParams, scheduleEventsData?.appointments, viewMode]);

  useEffect(() => {
    for (const appointment of filteredAppointments) {
      const patch = optimisticAppointmentPatches[appointment.appointment_id];
      if (isOptimisticAppointmentPatchConfirmed(appointment, patch)) {
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
      }
    }
  }, [dispatch, filteredAppointments, optimisticAppointmentPatches]);

  const appointmentsWithOptimisticPatches = useMemo(
    () =>
      filteredAppointments.map(appointment =>
        applyOptimisticAppointmentPatch(
          appointment,
          optimisticAppointmentPatches[appointment.appointment_id],
        ),
      ),
    [filteredAppointments, optimisticAppointmentPatches],
  );

  const {
    data: nextAppointmentData,
    isLoading: isNextAppointmentQueryLoading,
    isFetching: isNextAppointmentFetching,
    refetch: refetchNextAppointment,
  } = useGetNextAppointmentQuery(
    {
      num: 1,
    },
    {
      skip: isTutorialMode,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true, // Prevent stale data when navigating back
    },
  );

  // Determine final data based on tutorial mode
  const appointments = isTutorialMode ? mockAppointments : appointmentsWithOptimisticPatches;
  const nextAppointment = isTutorialMode
    ? mockAppointments[0] || null
    : applyOptimisticAppointmentPatch(
        nextAppointmentData?.items?.[0] || null,
        nextAppointmentData?.items?.[0]
          ? optimisticAppointmentPatches[nextAppointmentData.items[0].appointment_id]
          : undefined,
      );

  // Loading states from RTK Query (not Redux)
  const isLoading = !isTutorialMode && isScheduleEventsLoading;
  const isNextAppointmentLoading = !isTutorialMode && isNextAppointmentQueryLoading;
  const isBackgroundLoading =
    !isTutorialMode && (isScheduleEventsFetching || isNextAppointmentFetching);
  const isError = !isTutorialMode && isScheduleEventsError;

  // Simple refresh functions that rely on RTK Query's built-in state management
  const refreshAppointments = useCallback(async () => {
    await Promise.all([refetchScheduleEvents(), refetchNextAppointment()]);
  }, [refetchNextAppointment, refetchScheduleEvents]);

  const refreshNextEncounter = useCallback(async () => {
    await refetchNextAppointment();
  }, [refetchNextAppointment]);

  // Client state management hooks
  const viewControls = useAppointmentViewControls();
  const interactionControls = useAppointmentInteractions();
  const tutorialControls = useAppointmentTutorialMode();

  return {
    // Server data (from RTK Query or tutorial mock data)
    appointments,
    nextAppointment,

    // Loading states (from RTK Query)
    isLoading,
    isNextAppointmentLoading,
    isBackgroundLoading,
    isError,

    // Client state controls (from Redux)
    ...viewControls,
    ...interactionControls,
    ...tutorialControls,

    // Add displayMode separately since it's now part of viewControls
    displayMode,

    // Refresh functions (RTK Query)
    refreshAppointments,
    refreshNextEncounter,
  };
}

export const applyOptimisticAppointmentPatch = <T extends AppointmentResponse | null | undefined>(
  appointment: T,
  patch: Partial<AppointmentResponse> | undefined,
): T => {
  if (!appointment || !patch) return appointment;

  const { block_state: blockStatePatch, ...rest } = patch;
  const merged = {
    ...appointment,
    ...rest,
  } as AppointmentResponse;

  if (blockStatePatch && appointment.block_state) {
    merged.block_state = {
      ...appointment.block_state,
      ...blockStatePatch,
      qualifiers: {
        ...appointment.block_state.qualifiers,
        ...blockStatePatch.qualifiers,
      },
    };
  } else {
    merged.block_state = appointment.block_state;
  }

  return merged as T;
};

export const isOptimisticAppointmentPatchConfirmed = (
  appointment: AppointmentResponse | null | undefined,
  patch: Partial<AppointmentResponse> | undefined,
): boolean => {
  if (!appointment || !patch) return false;

  return Object.entries(patch).every(([key, value]) => {
    if (isOptimisticMetadataKey(key) || value === undefined) return true;
    if (key === 'provider') return true;
    if (key === 'patient_id') {
      return value == null ? !appointment.patient : appointment.patient?.patient_id === value;
    }
    if (key === 'patient') {
      const patientPatch = value as { patient_id?: string | null } | null;
      return patientPatch?.patient_id
        ? appointment.patient?.patient_id === patientPatch.patient_id
        : !appointment.patient;
    }
    return (appointment as Record<string, unknown>)[key] === value;
  });
};

const isOptimisticMetadataKey = (key: string): boolean =>
  key === 'block_state' ||
  key.endsWith('_at') ||
  key.endsWith('_source') ||
  key.endsWith('_reason') ||
  key.endsWith('_actor_type') ||
  key.endsWith('_user_id');

// Utility function to filter appointments based on view mode and date range
function filterAppointmentsForView(
  appointments: AppointmentResponse[],
  queryParams: AppointmentQueryParams,
  viewMode: ViewMode,
): AppointmentResponse[] {
  // Always filter by current date range from query params
  const startDate = new Date(queryParams.start_date);
  const endDate = new Date(queryParams.end_date);
  const providerIds = queryParams.provider_ids ? new Set(queryParams.provider_ids) : null;
  const patientIds = queryParams.patient_ids ? new Set(queryParams.patient_ids) : null;

  return appointments.filter(appointment => {
    if (!appointment.start_time) return false;
    const appointmentDate = new Date(appointment.start_time);
    if (appointmentDate < startDate || appointmentDate > endDate) return false;
    const providerId = appointment.provider_id ?? undefined;
    const patientId = appointment.patient?.patient_id ?? undefined;
    if (providerIds && (!providerId || !providerIds.has(providerId))) return false;
    if (patientIds && (!patientId || !patientIds.has(patientId))) return false;
    return true;
  });
}

/**
 * Hook for getting appointments for a specific date
 * Now uses RTK Query data directly
 */
export function useAppointmentsForDate(date: Date) {
  const { appointments } = useAppointments();

  return useMemo(() => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    const nextDay = new Date(targetDate);
    nextDay.setDate(nextDay.getDate() + 1);

    return appointments.filter(appointment => {
      if (!appointment.start_time) return false;
      const appointmentDate = new Date(appointment.start_time);
      return appointmentDate >= targetDate && appointmentDate < nextDay;
    });
  }, [appointments, date]);
}

// Helper function to create mock appointments (moved from context)
function createMockAppointments(): AppointmentResponse[] {
  const now = new Date();

  // Create appointment for current time
  const currentTime = new Date(now);

  // Create appointment for 2 hours from now
  const twoHoursFromNow = new Date(now);
  twoHoursFromNow.setHours(now.getHours() + 2);

  // Create appointment for tomorrow around same time
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Create appointment for 2 days later, 3.5 hours from current time
  const twoDaysLater = new Date(now);
  twoDaysLater.setDate(twoDaysLater.getDate() + 2);
  twoDaysLater.setHours(now.getHours() + 3);
  twoDaysLater.setMinutes(now.getMinutes() + 30);

  return [
    {
      appointment_id: 'mock-appointment-1',
      provider_id: 'mock-provider-1',
      start_time: currentTime.toISOString(),
      duration: 60,
      status: 'scheduled',
      created_at: new Date(currentTime.getTime() - 86400000).toISOString(),
      updated_at: new Date(currentTime.getTime() - 86400000).toISOString(),
      encounter_type_id: 'encounter-type-1',
      organization_id: 'org-1',
      patient: {
        patient_id: 'mock-patient-1',
        name: 'John Doe',
        dob: '1980-01-01',
        identifier: 'P12345',
      },
      series: null,
      encounter: null,
    },
    {
      appointment_id: 'mock-appointment-2',
      provider_id: 'mock-provider-1',
      start_time: twoHoursFromNow.toISOString(),
      duration: 30,
      status: 'confirmed',
      created_at: new Date(now.getTime() - 172800000).toISOString(),
      updated_at: new Date(now.getTime() - 172800000).toISOString(),
      encounter_type_id: 'encounter-type-2',
      organization_id: 'org-1',
      patient: {
        patient_id: 'mock-patient-2',
        name: 'Jane Smith',
        dob: '1985-05-15',
        identifier: 'P23456',
      },
      series: null,
      encounter: null,
    },
    {
      appointment_id: 'mock-appointment-3',
      provider_id: 'mock-provider-1',
      start_time: tomorrow.toISOString(),
      duration: 45,
      status: 'scheduled',
      created_at: new Date(now.getTime() - 259200000).toISOString(),
      updated_at: new Date(now.getTime() - 259200000).toISOString(),
      encounter_type_id: 'encounter-type-3',
      organization_id: 'org-1',
      patient: {
        patient_id: 'mock-patient-3',
        name: 'Robert Johnson',
        dob: '1975-10-20',
        identifier: 'P34567',
      },
      series: null,
      encounter: null,
    },
    {
      appointment_id: 'mock-appointment-4',
      provider_id: 'mock-provider-1',
      start_time: twoDaysLater.toISOString(),
      duration: 60,
      status: 'scheduled',
      created_at: new Date(now.getTime() - 345600000).toISOString(),
      updated_at: new Date(now.getTime() - 345600000).toISOString(),
      encounter_type_id: 'encounter-type-4',
      organization_id: 'org-1',
      patient: {
        patient_id: 'mock-patient-4',
        name: 'Emily Wilson',
        dob: '1990-08-12',
        identifier: 'P45678',
      },
      series: null,
      encounter: null,
    },
  ];
}
